import React from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  Header,
  Container,
  Loader,
  Divider,
} from '@gasbuddy/react-components';

import Button from '../button';
import DeleteConfirmationModal from '../delete-confirmation-modal';

import styles from './styles.css';

const { gTagEvent } = require('../../../lib/global-site-tag').default;

export default function BPModal({
  children,
  title,
  gTagTitle,
  onSave,
  onClose,
  onDelete,
  saveButtonText,
  deleteButtonText,
  deleteConfirmationMessage,
  disableSave,
  loading,
  show: showProps,
  ctaButtons,
  headerSize,
  button,
  background,
  size,
  ...rest
}) {
  const [showModal, setShowModal] = React.useState(showProps);
  const [showConfirmDeleteDialog, setShowConfirmDeleteDialog] = React.useState(false);

  React.useEffect(() => {
    setShowModal(showProps);
  }, [showProps]);

  const sendGAModalEvent = (buttonText) => {
    gTagEvent(gTagTitle || `${title} Popup`, buttonText);
  };

  const handleOnSave = async () => {
    // if the onSave method requires validate it will return a boolean
    // if the call was validated and completed we will call onClose, otherwise don't call onClose as the modal needs to stay open
    // if the call returns undefined it means no validation was needed (majority of modal cases) so we'll fire the onClose method
    const isValidated = await onSave();
    if (typeof isValidated === 'boolean') {
      if (isValidated) {
        handleOnClose();
        sendGAModalEvent(saveButtonText);
      }
    } else {
      handleOnClose();
      sendGAModalEvent(saveButtonText);
    }
  };

  const handleOnOpen = () => {
    sendGAModalEvent('Open Popup');
  };

  const handleOnClose = () => {
    if (onClose) {
      onClose();
    }
    setShowModal(false);
  };

  const handleOnDeleteConfirm = () => {
    if (deleteConfirmationMessage?.length && !showConfirmDeleteDialog) {
      setShowConfirmDeleteDialog(true);
    } else {
      onDelete();
      handleOnClose();
      if (showConfirmDeleteDialog) {
        setShowConfirmDeleteDialog(false);
      }
    }
  };

  return (
    <Modal
      forceIsShowing={showModal}
      onOpen={handleOnOpen}
      onClose={handleOnClose}
      size={size}
      content={(
        <>
          {title && (
          <>
            <Header as={headerSize} size="sm" className={styles.modalHeader}>
              {title}
            </Header>
            {!background && <Divider />}
          </>
          )}
          <Container className={`${styles.modalContentContainer} ${background ? styles.modalBackground : ''}`}>
            <div className={styles.modalContent}>
              {loading ? (
                <Loader size="lg" className={styles.loader} />
              ) : children}
            </div>
          </Container>
          {deleteButtonText || saveButtonText || ctaButtons.length ? (
            <Container className={styles.ctaButtons}>
              {deleteButtonText && <Button tertiary wide onClick={handleOnDeleteConfirm}>{deleteButtonText}</Button>}
              {deleteButtonText && deleteConfirmationMessage && (
                <DeleteConfirmationModal
                  show={showConfirmDeleteDialog}
                  deleteConfirmationMessage={deleteConfirmationMessage}
                  onDeleteConfirm={handleOnDeleteConfirm}
                  deleteButtonText={deleteButtonText}
                />
              )}
              {ctaButtons}
              {saveButtonText && <Button primary wide onClick={handleOnSave} disabled={disableSave}>{saveButtonText}</Button>}
            </Container>
          ) : null}
        </>
      )}
      {...rest}
    >
      {button && <button.type {...button.props} onClick={() => setShowModal(true)} />}
    </Modal>
  );
}

BPModal.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.string,
  gTagTitle: PropTypes.string,
  onSave: PropTypes.func,
  onClose: PropTypes.func,
  onDelete: PropTypes.func,
  saveButtonText: PropTypes.string,
  deleteButtonText: PropTypes.string,
  deleteConfirmationMessage: PropTypes.string,
  disableSave: PropTypes.bool,
  loading: PropTypes.bool,
  show: PropTypes.bool,
  ctaButtons: PropTypes.arrayOf(PropTypes.node),
  headerSize: 'h1' || 'h2' || 'h3' || 'h4' || 'h5' || 'h6',
  button: PropTypes.node,
  background: PropTypes.bool,
  size: 'xs' || 'sm' || 'md' || 'lg',
};

BPModal.defaultProps = {
  title: '',
  gTagTitle: null,
  onSave: () => { },
  onClose: () => { },
  onDelete: () => { },
  saveButtonText: null,
  deleteButtonText: null,
  deleteConfirmationMessage: null,
  disableSave: false,
  loading: false,
  show: false,
  ctaButtons: [],
  headerSize: 'h2',
  button: null,
  background: false,
  size: 'sm',
};
